import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { buildCards } from '../data/content';
const AUTOPLAY_MS = 4800;
const RESUME_AFTER_MS = 9000;
const SWIPE_THRESHOLD = 40;

/** Slide width as a percentage of the viewport track: 1 up on mobile, 2 on tablet, center mode on desktop. */
function useSlideWidth(): number {
  const [width, setWidth] = useState(100);
  useEffect(() => {
    const resolve = () => {
      const w = window.innerWidth;
      setWidth(w >= 1024 ? 64 : w >= 640 ? 50 : 100);
    };
    resolve();
    window.addEventListener('resize', resolve);
    return () => window.removeEventListener('resize', resolve);
  }, []);
  return width;
}
export function BuildCardCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slideWidth = useSlideWidth();
  const resumeTimer = useRef<number | null>(null);
  const pointerStart = useRef<number | null>(null);
  const holdAutoplay = useCallback(() => {
    setPaused(true);
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => setPaused(false), RESUME_AFTER_MS);
  }, []);
  const goTo = useCallback((next: number) => {
    setIndex((next + buildCards.length) % buildCards.length);
    holdAutoplay();
  }, [holdAutoplay]);
  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => setIndex(prev => (prev + 1) % buildCards.length), AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [paused]);
  useEffect(() => () => {
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
  }, []);
  const offset = 50 - (index + 0.5) * slideWidth;
  return <div className="mt-6 w-full" role="group" aria-roledescription="carousel" aria-label="Sample build cards" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-38" data-mp="1wblk0ugcakw0">
      <div className="relative overflow-hidden" onPointerDown={e => {
      pointerStart.current = e.clientX;
    }} onPointerUp={e => {
      const start = pointerStart.current;
      pointerStart.current = null;
      if (start === null) return;
      const delta = e.clientX - start;
      if (Math.abs(delta) > SWIPE_THRESHOLD) goTo(index + (delta < 0 ? 1 : -1));
    }} data-id="element-39" data-mp="1uvrabjpoi46k">
        <motion.ul className="flex touch-pan-y list-none" animate={{
        x: `${offset}%`
      }} transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-40" data-mp="2dnk5nks801hg">
          {buildCards.map((card, i) => {
          const isActive = i === index;
          return <li key={card.src} className="shrink-0 px-2 sm:px-3" style={{
            flexBasis: `${slideWidth}%`
          }} aria-hidden={!isActive} data-id="element-41" data-mp="31dxs8vs1d84n" data-mpi={i}>
                <motion.div animate={{
              scale: isActive ? 1 : 0.92,
              opacity: isActive ? 1 : 0.5
            }} transition={{
              duration: 0.3,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex h-[360px] items-center justify-center rounded-frame border border-foreground/10 bg-background p-3 shadow-frame sm:h-[460px] sm:p-4" data-id="element-42" data-mp="33yvpg7mryk8b">
                  <img src={card.src} alt={card.alt} loading={i <= 1 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="max-h-full max-w-full object-contain" data-id="element-43" data-mp="3muvh1hbublrd" />
                </motion.div>
              </li>;
        })}
        </motion.ul>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4" data-id="element-44" data-mp="ag309v6q31p7">
        <button type="button" onClick={() => goTo(index - 1)} aria-label="Previous build card" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-foreground/15 bg-card text-foreground transition-colors duration-150 ease-out hover:bg-secondary" data-id="element-45" data-mp="egv9wpjib8m0">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-46" data-mp="1kz4k37hvd4qt" />
        </button>

        <div className="flex flex-1 flex-col items-center gap-2" data-id="element-47" data-mp="1gvyg66i8bpni">
          <div className="flex flex-wrap items-center justify-center gap-2" data-id="element-48" data-mp="t52iisr3e08c">
            {buildCards.map((card, i) => <button key={card.src} type="button" onClick={() => goTo(i)} aria-label={`Show ${card.title}`} aria-current={i === index} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-6 bg-accent' : 'w-2 bg-foreground/20 hover:bg-foreground/35'}`} data-id="element-49" data-mp="o82jv2wknzr9" data-mpi={i} />)}
          </div>
          <p className="text-[11px] font-bold tracking-[0.14em] text-foreground/50" aria-live="off" data-id="element-50" data-mp="2h2k0tdrg49qk">
            {String(index + 1).padStart(2, '0')} / {String(buildCards.length).padStart(2, '0')}
          </p>
        </div>

        <button type="button" onClick={() => goTo(index + 1)} aria-label="Next build card" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-foreground/15 bg-card text-foreground transition-colors duration-150 ease-out hover:bg-secondary" data-id="element-51" data-mp="2770ar7zkc59q">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-52" data-mp="34v0nk1m4opw7" />
        </button>
      </div>
    </div>;
}